// contexts/DataContext.js
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { FfttService } from '../services/FFTTService';
import { parseFrenchDate, matchDate } from '../utils/utils';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

const parseDivision = (liendivision) => ({
  cx_poule: liendivision.split('cx_poule=')[1].split('&')[0],
  D1: liendivision.split('D1=')[1].split('&')[0],
});

const matchConcerneEquipe = (match, equipe) => {
  const libEquipe = equipe.libequipe.split('-')[0].slice(0, -1);
  return (match.equa ?? '') === libEquipe || (match.equb ?? '') === libEquipe;
};

export const DataProvider = ({ children }) => {
  // Les vues concernées lancent leur fetch au montage : on démarre donc en
  // « chargement » pour que le pré-rendu et le 1er rendu client soient identiques.
  const [joueurs, setJoueurs] = useState([]);
  const [joueursLoading, setJoueursLoading] = useState(true);
  const [joueursError, setJoueursError] = useState(null);

  const [topJoueurs, setTopJoueurs] = useState([]);
  const [topJoueursLoading, setTopJoueursLoading] = useState(true);
  const [topJoueursError, setTopJoueursError] = useState(null);

  const [equipes, setEquipes] = useState([]);
  const [equipesLoading, setEquipesLoading] = useState(true);
  const [equipesError, setEquipesError] = useState(null);

  const [equipesProchainMatchs, setEquipesProchainMatchs] = useState([]);

  const [matchs, setMatchs] = useState({});
  const [matchsLoading, setMatchsLoading] = useState(true);
  const [matchsError, setMatchsError] = useState(null);

  const [prochainsMatchs, setProchainsMatchs] = useState({});
  const [prochainsMatchsLoading, setProchainsMatchsLoading] = useState(true);
  const [prochainsMatchsError, setProchainsMatchsError] = useState(null);

  // Empêche les doubles requêtes (StrictMode, effets qui se relancent) sans
  // dépendre de l'état de rendu : les fonctions restent stables.
  const done = useRef({});

  const fetchJoueurs = useCallback(async (force = false) => {
    if (done.current.joueurs && !force) return;
    done.current.joueurs = true;
    setJoueursLoading(true);
    setJoueursError(null);
    try {
      const data = await new FfttService().fetchJoueursByPoints();
      setJoueurs(Array.isArray(data) ? data : []);
    } catch (err) {
      done.current.joueurs = false;
      setJoueursError(err.message);
    } finally {
      setJoueursLoading(false);
    }
  }, []);

  const fetchTopJoueurs = useCallback(async (force = false) => {
    if (done.current.topJoueurs && !force) return;
    done.current.topJoueurs = true;
    setTopJoueursLoading(true);
    setTopJoueursError(null);
    try {
      const data = await new FfttService().fetchJoueursByPoints(true);
      setTopJoueurs(Array.isArray(data) ? data : []);
    } catch (err) {
      done.current.topJoueurs = false;
      setTopJoueursError(err.message);
    } finally {
      setTopJoueursLoading(false);
    }
  }, []);

  const fetchEquipes = useCallback(async (force = false) => {
    if (done.current.equipes && !force) return;
    done.current.equipes = true;
    setEquipesLoading(true);
    setEquipesError(null);
    try {
      const data = await new FfttService().fetchEquipes(false);
      setEquipes(Array.isArray(data) ? data : []);
    } catch (err) {
      done.current.equipes = false;
      setEquipesError(err.message);
    } finally {
      setEquipesLoading(false);
    }
  }, []);

  const fetchProchainsMatchs = useCallback(async (force = false) => {
    if (done.current.prochainsMatchs && !force) return;
    done.current.prochainsMatchs = true;
    setProchainsMatchsLoading(true);
    setProchainsMatchsError(null);
    try {
      const ffttService = new FfttService();
      const equipesData = await ffttService.fetchEquipes(true);
      setEquipesProchainMatchs(Array.isArray(equipesData) ? equipesData : []);

      const now = new Date();
      const limiteBasse = new Date(now);
      limiteBasse.setDate(limiteBasse.getDate() - 3);

      // Une requête par équipe, mais en parallèle.
      const résultats = await Promise.all(
        equipesData.map(async (equipe) => {
          const matchsEquipe = await ffttService.fetchMatchs(parseDivision(equipe.liendivision));
          const prochain = (Array.isArray(matchsEquipe) ? matchsEquipe : [])
            .filter(
              (match) =>
                matchConcerneEquipe(match, equipe) &&
                parseFrenchDate(matchDate(match)) >= limiteBasse
            )
            .sort((a, b) => parseFrenchDate(matchDate(a)) - parseFrenchDate(matchDate(b)))[0];
          return [equipe.idequipe, prochain];
        })
      );

      setProchainsMatchs(Object.fromEntries(résultats));
    } catch (err) {
      done.current.prochainsMatchs = false;
      setProchainsMatchsError(err.message);
    } finally {
      setProchainsMatchsLoading(false);
    }
  }, []);

  const fetchMatchsForEquipe = useCallback(async (equipe) => {
    const key = equipe.liendivision;
    let alreadyLoaded = false;
    setMatchs((prev) => {
      alreadyLoaded = Boolean(prev[key]);
      return prev;
    });
    if (alreadyLoaded) return;

    setMatchsLoading(true);
    setMatchsError(null);
    try {
      const matchsData = await new FfttService().fetchMatchs(parseDivision(key));
      const filtreMatch = (Array.isArray(matchsData) ? matchsData : []).filter((match) =>
        matchConcerneEquipe(match, equipe)
      );
      setMatchs((prev) => ({ ...prev, [key]: filtreMatch }));
    } catch (err) {
      setMatchsError(err.message);
    } finally {
      setMatchsLoading(false);
    }
  }, []);

  const fetchAllMatchs = useCallback(async (equipesData, force = false) => {
    if (done.current.allMatchs && !force) return;
    done.current.allMatchs = true;
    setMatchsLoading(true);
    setMatchsError(null);
    try {
      const ffttService = new FfttService();
      const résultats = await Promise.all(
        equipesData.map(async (equipe) => {
          const matchsEquipe = await ffttService.fetchMatchs(parseDivision(equipe.liendivision));
          const filtreMatch = (Array.isArray(matchsEquipe) ? matchsEquipe : []).filter((match) =>
            matchConcerneEquipe(match, equipe)
          );
          return [equipe.liendivision, filtreMatch];
        })
      );
      setMatchs(Object.fromEntries(résultats));
    } catch (err) {
      done.current.allMatchs = false;
      setMatchsError(err.message);
    } finally {
      setMatchsLoading(false);
    }
  }, []);

  const value = {
    joueurs,
    joueursLoading,
    joueursError,
    fetchJoueurs,

    topJoueurs,
    topJoueursLoading,
    topJoueursError,
    fetchTopJoueurs,

    equipes,
    equipesLoading,
    equipesError,
    fetchEquipes,

    matchs,
    matchsLoading,
    matchsError,
    fetchMatchsForEquipe,
    fetchAllMatchs,

    prochainsMatchs,
    prochainsMatchsLoading,
    prochainsMatchsError,
    equipesProchainMatchs,
    fetchProchainsMatchs,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
